import React from 'react'
import { useListState } from '@mantine/hooks'
import { IconSearch } from '@tabler/icons-react'
import { useForm, zodResolver } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { Button, Fieldset, Loader, Modal, Text } from '@mantine/core'
import { useMutation, useQuery } from '@tanstack/react-query'

import {
  CommitBaseType,
  CreateCommitType,
  UpdateCommitType,
  CreateCommitSchema,
} from '@customTypes/commit'
import { ModalProps } from '@customTypes/page'
import { RawMaterialBaseType } from '@customTypes/rawMaterial'

import QueryKeys from '@constants/queryKeys'
import { CommitAction } from '@constants/commits'

import DataRepo from '@api/datasource/data'
import queryClient from '@api/datasource/query'

import { useIsMobile, useTableMinWidth } from '@hooks/mobile'

import { ErrorService } from '@utils/error'
import { buildSelectOptions, filterSelect } from '@utils/form'
import { isLoadingMutation, isLoadingOrRefetchQuery } from '@utils/network'

import Input from '@components/shared/input'
import { Dropzone } from '@components/shared/dropzone'
import ConfirmationModal from '@components/shared/confirmationModal'
import TableCustom, { GenericColumnType } from '@components/shared/table'
import { WarehouseType } from '@customTypes/warehouse'
import { ellipsis } from '@utils/string'
import { ReceiptType } from '@customTypes/receipt'
import { LIMIT_SELECT_SEARCH } from '@constants/app'

type RMItem = WarehouseType['rawMaterials'][0] & { stock: number }

type ReceiptItem = WarehouseType['receipts'][0] & { stock: number }

export type CommitFormType = {
  warehouseID: string
  warehouseName: string
  rawMaterialsWarehouse: RMItem[]
  receiptsWarehouse: ReceiptItem[]
  commit?: CommitBaseType
}

const INITIAL: CreateCommitType['commit'] = {
  message: '',
  action: 'consume',
  attachment: null,
  rawMaterials: [],
  receipts: [],
}

const CommitForm = (props: ModalProps<CommitFormType>) => {
  const { open, modal, data = {} as CommitFormType, size = 'lg', onClose: outerOnClose } = props

  const [confirmExit, setConfirmExit] = React.useState(false)

  const isMobile = useIsMobile()

  const minWidth = useTableMinWidth(isMobile, true)

  const [selectedRowsRMs, handlersSelectedRMs] = useListState<string>([])

  const [selectedRowsReceipts, handlersSelectedReceipts] = useListState<string>([])

  const formCommit = useForm<CreateCommitType['commit']>({
    initialValues: INITIAL,
    validate: zodResolver(CreateCommitSchema.shape.commit),
  })

  const commitCreateMutation = useMutation<CommitBaseType, ErrorService, CreateCommitType>({
    mutationFn: async (data) => {
      const response = await DataRepo.warehouseEntitiesService.createCommit(data)

      await queryClient.invalidateQueries({
        predicate: (query) => [QueryKeys.GET_COMMITS_KEY].includes(query.queryKey[0] as string),
        refetchType: 'all',
      })

      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_WAREHOUSE_KEY, warehouseID],
        refetchType: 'all',
        exact: true,
      })

      return response
    },
    onSettled: (_, error) => {
      if (error) {
        return notifications.show({
          color: 'red',
          title: 'Error',
          message: error.message ?? 'Error al crear el registro',
        })
      }

      notifications.show({
        color: 'green',
        title: 'Éxito',
        message: 'Registro creado correctamente',
      })

      onClose?.()
    },
  })

  const commitUpdateMutation = useMutation<void, ErrorService, UpdateCommitType>({
    mutationFn: async (data) => {
      const response = await DataRepo.warehouseEntitiesService.updateCommit(data)

      await queryClient.invalidateQueries({
        predicate: (query) => [QueryKeys.GET_COMMITS_KEY].includes(query.queryKey[0] as string),
        refetchType: 'all',
      })

      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_WAREHOUSE_KEY, warehouseID],
        refetchType: 'all',
        exact: true,
      })

      return response
    },
    onSettled: (_, error) => {
      if (error) {
        return notifications.show({
          color: 'red',
          title: 'Error',
          message: error.message ?? 'Error al actualizar el registro',
        })
      }

      notifications.show({
        color: 'green',
        title: 'Éxito',
        message: 'Registro actualizado correctamente',
      })

      onClose?.()
    },
  })

  const rawMaterialsQuery = useQuery<RawMaterialBaseType[], ErrorService>({
    queryKey: [QueryKeys.GET_MATERIALS_KEY],
    queryFn: async () => {
      return await DataRepo.constructionEntitiesService.getRawMaterials({
        orderDir: 'asc',
      })
    },
  })

  const receiptsQuery = useQuery<ReceiptType[], ErrorService, ReceiptType[]>({
    queryKey: [QueryKeys.GET_RECEIPTS_EXTENDED_KEY],
    queryFn: async () => {
      const response = await DataRepo.constructionEntitiesService.getReceiptsExtended({})

      return response
    },
  })

  React.useEffect(() => {
    if (open && data?.commit) {
      formCommit.setValues(data.commit)
    } else if (!open) {
      formCommit.setValues(INITIAL)
      handlersSelectedRMs.setState([])
      handlersSelectedReceipts.setState([])
    }
    formCommit.resetDirty()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.commit, open])

  const isLoading = isLoadingMutation(commitCreateMutation, commitUpdateMutation)

  const { rawMaterials, receipts, action } = formCommit.getValues()

  const { warehouseID, rawMaterialsWarehouse = [], receiptsWarehouse = [] } = data

  const sourceRawMaterials =
    action === 'refill' ? parseRawMaterials(rawMaterialsQuery.data || []) : rawMaterialsWarehouse

  const sourceReceipts = (
    action === 'refill' ? parseReceipts(receiptsQuery.data || []) : receiptsWarehouse
  ).filter((r) => r.rawMaterials.length > 0)

  const content = warehouseID && (
    <form
      className="cd-flex cd-flex-col cd-gap-y-[1rem]"
      onSubmit={formCommit.onSubmit((values) => {
        if (!warehouseID) return

        if (data.commit) {
          commitUpdateMutation.mutate({
            warehouseID,
            current: values,
            previous: data.commit,
          })
        } else {
          commitCreateMutation.mutate({
            warehouseID,
            commit: values,
          })
        }
      })}
    >
      <Input
        data={Object.values([CommitAction.consume, CommitAction.refill])}
        label="Tipo de acción"
        typeInput="segmentedControl"
        {...formCommit.getInputProps('action')}
        onBlur={() => formCommit.validateField('action')}
        onChange={(value) => {
          formCommit.setFieldValue('action', value as CommitBaseType['action'], {
            forceUpdate: true,
          })
          formCommit.setFieldValue('rawMaterials', [], { forceUpdate: true })
        }}
      />

      <Input
        autosize
        label="Mensaje"
        maxRows={4}
        minRows={2}
        placeholder="Describa la acción realizada"
        typeInput="textarea"
        {...formCommit.getInputProps('message')}
        onBlur={() => formCommit.validateField('message')}
      />

      <Dropzone
        files={formCommit.values.attachment ? [formCommit.values.attachment] : []}
        label="Adjuntar archivo"
        maxFiles={1}
        multiple={false}
        onDrop={(files) => formCommit.setFieldValue('attachment', files[0])}
      />

      <Fieldset legend="Materias primas">
        <Input
          clearable
          searchable
          className="cd-my-[0.5rem]"
          data={buildSelectOptions<RMItem>({
            data: sourceRawMaterials.filter(
              (item) => !rawMaterials.some((rm) => rm.uid === item.uid),
            ),
            value: 'uid',
            label: (item) =>
              `${item.name} ${item.description?.length ? `(${ellipsis(item.description)})` : ''}`,
          })}
          filter={(input) =>
            filterSelect({
              limit: LIMIT_SELECT_SEARCH,
              search: input.search,
              options: input.options,
            })
          }
          nothingFoundMessage={
            <div>
              <Text c="dimmed" className="cd-text-sm">
                {rawMaterialsQuery.data?.length
                  ? 'No se pueden agregar materias primas existentes'
                  : 'No hay materias primas'}
              </Text>
            </div>
          }
          placeholder="Escribe para buscar materia prima existente"
          rightSection={
            isLoadingOrRefetchQuery(rawMaterialsQuery) ? (
              <Loader size={18} />
            ) : (
              <IconSearch size={18} />
            )
          }
          typeInput="select"
          value={undefined}
          withCheckIcon={false}
          onChange={(value) => {
            const rawMaterial = sourceRawMaterials.find((item) => item.uid === value)

            if (!rawMaterial) return

            formCommit.insertListItem('rawMaterials', {
              ...rawMaterial,
              quantity: 1,
            })
          }}
        />

        <TableCustom<CommitBaseType['rawMaterials'][0]>
          columns={buildColumnsRMs()}
          data={rawMaterials}
          keyId="uid"
          minWidth={minWidth}
          noDataMessage="No se han agregado materias primas"
          selectedRows={selectedRowsRMs}
          onChangeSelected={handlersSelectedRMs}
          onDelete={(ids) => {
            const filtered = rawMaterials.filter((item) => !ids.includes(item.uid))

            formCommit.setFieldValue('rawMaterials', filtered, { forceUpdate: true })
            handlersSelectedRMs.setState([])
          }}
        />
      </Fieldset>

      <Fieldset legend="Recetas">
        <Input
          clearable
          searchable
          className="cd-my-[0.5rem]"
          data={buildSelectOptions<ReceiptItem>({
            data: sourceReceipts.filter((item) => !receipts.some((rm) => rm.uid === item.uid)),
            value: 'uid',
            label: (item) => `${item.identifier} ${item.name}`,
          })}
          filter={(input) =>
            filterSelect({
              limit: LIMIT_SELECT_SEARCH,
              search: input.search,
              options: input.options,
            })
          }
          nothingFoundMessage={
            <div>
              <Text c="dimmed" className="cd-text-sm">
                {receiptsQuery.data?.length
                  ? 'No se pueden agregar recetas existentes'
                  : 'No hay recetas'}
              </Text>
            </div>
          }
          placeholder="Escribe para buscar receta existente"
          rightSection={
            isLoadingOrRefetchQuery(receiptsQuery) ? <Loader size={18} /> : <IconSearch size={18} />
          }
          typeInput="select"
          value={undefined}
          withCheckIcon={false}
          onChange={(value) => {
            const receipt = sourceReceipts.find((item) => item.uid === value)

            if (!receipt) return

            formCommit.insertListItem('receipts', {
              ...receipt,
              quantity: 1,
            })
          }}
        />

        <TableCustom<CommitBaseType['receipts'][0]>
          columns={buildColumnsReceipts()}
          data={receipts}
          keyId="uid"
          minWidth={minWidth}
          noDataMessage="No se han agregado recetas"
          selectedRows={selectedRowsReceipts}
          onChangeSelected={handlersSelectedReceipts}
          onDelete={(ids) => {
            const filtered = receipts.filter((item) => !ids.includes(item.uid))

            formCommit.setFieldValue('receipts', filtered, { forceUpdate: true })
            handlersSelectedReceipts.setState([])
          }}
        />
      </Fieldset>

      <Button loaderProps={{ type: 'dots' }} loading={isLoading} type="submit">
        {data.commit ? 'Editar' : 'Crear'} registro
      </Button>
    </form>
  )

  if (modal) {
    return (
      <Modal
        centered
        fullScreen={isMobile}
        opened={Boolean(open)}
        size={size}
        title={`${data.commit ? 'Editar' : 'Nuevo'} registro de bodega ${data.warehouseName}`}
        onClose={() => {
          if (formCommit.isDirty()) {
            setConfirmExit(true)
          } else {
            onClose()
          }
        }}
      >
        {content}
        <ConfirmationModal
          cancelColor="gray"
          cancelText="Cancelar"
          confirmColor="red"
          confirmText="Descartar"
          open={confirmExit}
          title="Descartar cambios"
          onCancel={() => setConfirmExit(false)}
          onConfirm={onClose}
        >
          <Text className="cd-text-base">
            Has realizado cambios en el formulario, ¿Deseas cerrarlo y descartar los cambios?
          </Text>
        </ConfirmationModal>
      </Modal>
    )
  }

  return content

  function parseRawMaterials(rawMaterials: RawMaterialBaseType[]) {
    const rawMaterialItems: RMItem[] = []

    for (const rawMaterial of rawMaterials) {
      rawMaterialItems.push({
        alertStock: rawMaterial.alertStock,
        attachments: rawMaterial.attachments,
        createdAt: rawMaterial.createdAt,
        description: rawMaterial.description,
        descriptionProforma: rawMaterial.descriptionProforma,
        measure: rawMaterial.measure,
        name: rawMaterial.name,
        stock: Infinity,
        uid: rawMaterial.uid,
        updatedAt: rawMaterial.updatedAt,
        value: rawMaterial.value,
        deleted: rawMaterial.deleted,
      })
    }

    return rawMaterialItems
  }

  function parseReceipts(receipts: ReceiptType[]) {
    const receiptsItems: ReceiptItem[] = []

    for (const receipt of receipts) {
      receiptsItems.push({
        createdAt: receipt.createdAt,
        description: receipt.description,
        name: receipt.name,
        uid: receipt.uid,
        identifier: receipt.identifier,
        updatedAt: receipt.updatedAt,
        deleted: receipt.deleted,
        stock: Infinity,
        rawMaterials: receipt.rawMaterials,
        descriptionProforma: receipt.descriptionProforma,
      })
    }

    return receiptsItems
  }

  function buildColumnsRMs() {
    return [
      { key: 'name', label: 'Nombre', width: '50%', type: 'text' },
      {
        key: 'quantity',
        label: 'Cantidad',
        width: '30%',
        type: 'number',
        inputProps: {
          min: 1,
          maxKey: 'stock',
          defaultValue: (item) => item.quantity ?? 1,
          allowNegative: false,
          onChange: handleMaterialInput.bind(null, 'quantity'),
        },
      },
    ] as GenericColumnType<CommitBaseType['rawMaterials'][0]>[]
  }

  function buildColumnsReceipts() {
    return [
      { key: 'name', label: 'Nombre', width: '50%', type: 'text' },
      {
        key: 'quantity',
        label: 'Cantidad',
        width: '30%',
        type: 'number',
        inputProps: {
          min: 1,
          maxKey: 'stock',
          defaultValue: (item) => item.quantity ?? 1,
          allowNegative: false,
          onChange: handleReceiptInput.bind(null, 'quantity'),
        },
      },
    ] as GenericColumnType<CommitBaseType['receipts'][0]>[]
  }

  function handleReceiptInput(
    key: keyof CommitBaseType['receipts'][0],
    value: number | string | boolean,
    uid: string,
  ) {
    const receiptIdx = receipts.findIndex((item) => item.uid === uid)

    if (receiptIdx === -1) return

    formCommit.setFieldValue(`receipts.${receiptIdx}.${key}`, value, { forceUpdate: true })
  }

  function handleMaterialInput(
    key: keyof CommitBaseType['rawMaterials'][0],
    value: number | string | boolean,
    uid: string,
  ) {
    const rawMaterialIdx = rawMaterials.findIndex((item) => item.uid === uid)

    if (rawMaterialIdx === -1) return

    formCommit.setFieldValue(`rawMaterials.${rawMaterialIdx}.${key}`, value, { forceUpdate: true })
  }

  function onClose() {
    setConfirmExit(false)
    outerOnClose?.()
  }
}

export default CommitForm
